import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
} from '@mui/material';
import TextInputBox from '../../components/TextInputBox';
import ButtonBox from '../../components/ButtonBox';
import { useNavigate } from 'react-router-dom';
import { APPLICATION_ID, ROUTE_APPLICATION, ROUTE_APPLICATION_INFORMATION } from '../../constants';
import useApplicationStore from '../../stores/ApplicationStore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import ApplicationApi from '../../api/ApplicationApi';
import { addLocalStorage, getLocalStorage, getRandomId, removeLocalStorage, useError } from '../../utils/functions';
import { RedirectURI } from '../../types/ApplicationType';
import IconButtonBox from '../../components/IconButtonBox';

const ApplicationCreateEdit: React.FC = () => {
  const navigate = useNavigate();
  const { createMode, setCreateMode, name, setName, description, setDescription, website, setWebsite, redirectUri, setRedirectUri, selectedOptions, resetStore } = useApplicationStore();

  const { setError } = useError();

  const handleRoute = () => {
    navigate(createMode ? ROUTE_APPLICATION : ROUTE_APPLICATION_INFORMATION);
  }

  const handleCreate = async () => {
    let hasErr = false;

    if (!createMode) {
      const newRedirectURIs = [];
      const appsResponse = await ApplicationApi.getApp(getLocalStorage(APPLICATION_ID));
      if (appsResponse && appsResponse.data) {
        const data = appsResponse.data;
        if (data.redirectURIs) {
          const oldMap = new Map(data.redirectURIs.map((uri: RedirectURI) => [uri.id, uri.redirectURI]));

          for (let nowUri of redirectUri) {
            if (oldMap.has(nowUri.id)) {
              if (oldMap.get(nowUri.id) !== nowUri.redirectURI) {
                await ApplicationApi.deleteRedirectUri(data.id, nowUri.id);
                newRedirectURIs.push(nowUri.redirectURI);
              }
            } else {
              newRedirectURIs.push(nowUri.redirectURI);
            }
          }

          const nowMap = new Map(redirectUri.map((uri: RedirectURI) => [uri.id, uri.redirectURI]));
          for (let org of data.redirectURIs) {
            if (!nowMap.has(org.id)) {
              await ApplicationApi.deleteRedirectUri(data.id, org.id);
            }
          }
        }
      } else if (appsResponse.code) {
        setError(appsResponse.message);
        hasErr = true;
      }

      if (!hasErr) {
        for (let uri of newRedirectURIs) {
          const res = await ApplicationApi.createRedirectUri(getLocalStorage(APPLICATION_ID), uri);
          if (res.code) {
            setError(res.message);
            hasErr = true;
            break;
          }
        }

        const projectAccessResponse = await ApplicationApi.setProjectAccess(getLocalStorage(APPLICATION_ID), selectedOptions);
        if (projectAccessResponse.code) {
          setError(projectAccessResponse.message);
          hasErr = true;
        }

      }


    } else {
      const newUri = [] as string[];

      redirectUri.map((uri) => {
        newUri.push(uri.redirectURI);
      });

      const applicationResponse = await ApplicationApi.createApp(name, description, website, newUri);
      if (applicationResponse.code) {
        setError(applicationResponse.message);
        hasErr = true;
      } else {
        const projectAccessResponse = await ApplicationApi.setProjectAccess(applicationResponse?.data?.id, selectedOptions);
        if (projectAccessResponse.code) {
          setError(projectAccessResponse.message);
          hasErr = true;
        } else {
          removeLocalStorage(APPLICATION_ID);
          addLocalStorage(APPLICATION_ID, applicationResponse?.data?.id);

          const res = await ApplicationApi.createApiKey(applicationResponse?.data?.id);

          if (res.code) {
            setError(res.message);
            hasErr = true;
          }
        }
      }
    }

    if (!hasErr) {
      handleRoute();
    }
  }

  const handleDeleteItem = (position: number) => {
    setRedirectUri(redirectUri.filter((uri, index) => index !== position));
  };

  const handleChange = (index: number, value: string) => {
    const updatedUris = [...redirectUri];
    updatedUris[index].redirectURI = value;
    setRedirectUri(updatedUris);
  };

  const handleAdd = () => {
    setRedirectUri([...redirectUri, { id: getRandomId(), redirectURI: '' }]);
  };

  const queryApplication = async () => {
    try {
      const appsResponse = await ApplicationApi.getApp(getLocalStorage(APPLICATION_ID));

      if (appsResponse && appsResponse.data) {
        const data = appsResponse.data;
        setCreateMode(false);
        setName(data.name || '');
        setDescription(data.description || '');
        setWebsite(data.website || '');

        if (data.redirectURIs) {
          let uris = [] as RedirectURI[];
          for (let redirect of data.redirectURIs) {
            uris.push({ id: redirect.id, redirectURI: redirect.redirectURI });
          }

          setRedirectUri(uris);
        }

        if (data.projects) {
          let pro = [] as string[];
          let show = [] as string[];
          for (let project of data.projects) {
            pro.push(project.id);
            show.push(project.name);
          }

        }

      } else if (appsResponse.code) {
        setError(appsResponse.message);
      }
    } catch (error) {
      console.error("Error fetching appsResponse data:", error);
    }
  }

  useEffect(() => {
    if (getLocalStorage(APPLICATION_ID) !== '') {
      queryApplication();
    } else {
      resetStore();
    }

  }, []);

  return (
    <Box>
      <Typography className='layout-title'>
        {createMode ? 'Create an application' : 'Edit an application'}
      </Typography>
      <Paper sx={{ padding: 4, position: 'relative', width: '90%' }}>
        <Box className='line_box' />
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>Information</Typography>
        <Box mb={4} display="flex" gap={4}>
          <Box flex={1}>
            <TextInputBox
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. MagiCity"
              required={true}
              className='w400'
              disabled={!createMode}
            />

            <TextInputBox
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this application"
              className='w400'
              disabled={!createMode}
            />

            <TextInputBox
              label="Website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="e.g https://magicity.iiot.oringnet.cloud"
              className='w400'
              disabled={!createMode}
            />

            <Typography variant="h6" align="left" gutterBottom>
              Redirect URI<span style={{ color: 'red' }}>*</span>
            </Typography>
            {redirectUri.map((uri, index) => (
              <Box key={uri.id} sx={{ display: "flex", gap: 2, alignItems: "center", marginBottom: 2 }}>
                <TextInputBox
                  key={index}
                  value={uri.redirectURI}
                  onChange={(e) => handleChange(index, e.target.value)}
                  placeholder="Enter URI"
                  className="w400"
                />
                <IconButtonBox icon={<DeleteOutlineOutlinedIcon />} onClick={() => handleDeleteItem(index)} />
              </Box>
            ))}

            <Box>
              <ButtonBox
                variant="outlined"
                text='Add'
                onClick={handleAdd}
                icon={<AddIcon />} />
            </Box>
          </Box>

          <Box flex={1}>
            <MultiSelectDropdown />
          </Box>
        </Box>

        <Box className='action_box'>
          <ButtonBox
            variant="outlined"
            text="Cancel"
            onClick={handleRoute} />

          <ButtonBox
            variant="contained"
            text={createMode ? "Create" : "Save"}
            onClick={handleCreate} />
        </Box>
      </Paper >
    </Box >
  );
};

export default ApplicationCreateEdit;
